/* global uacapsGlobals */

/**
 * Handles form submission via AJAX.
 */
class FormHandler {
  /**
   * Initializes the form handler.
   *
   * @param {HTMLFormElement} form - The form to handle.
   */
  constructor(form) {
    this.form = form;
    this.alertPlaceholder = this.getAlertPlaceholder();
  }

  /**
   * Handles the form submission event.
   *
   * @param {Event} event - The submit event.
   */
  handleSubmission(event) {
    event.preventDefault();

    const formData = this.prepareFormData();

    if (this.isBotSubmission(formData)) {
      console.error('Honeypot field is filled. Possible bot submission.');
      return;
    }

    this.submitFormData(formData)
      .then((data) => this.handleResponse(data))
      .catch((error) => this.handleError(error));
  }

  /**
   * Prepares the form data with additional fields.
   *
   * @returns {FormData} - The prepared form data.
   */
  prepareFormData() {
    const formData = new FormData(this.form);
    formData.append('_uacaps_form_verify', uacapsGlobals.nonce);
    return formData;
  }

  /**
   * Checks for bot submissions using the honeypot field.
   *
   * @param {FormData} formData - The submitted form data.
   * @returns {boolean} - True if bot submission is detected, otherwise false.
   */
  isBotSubmission(formData) {
    return formData.get('_uacaps_honeypot') !== '';
  }

  /**
   * Submits the form data via AJAX.
   *
   * @param {FormData} formData - The form data to submit.
   * @returns {Promise<Object>} - A promise resolving to the server response.
   */
  async submitFormData(formData) {
    const response = await fetch(uacapsGlobals.ajax_url, {
      method: 'POST',
      headers: { 'X-Requested-With': 'XMLHttpRequest' },
      body: formData,
    });

    if (!response.ok) {
      throw new Error(`HTTP error! Status: ${response.status}`);
    }

    return response.json();
  }

  /**
   * Handles the server response after form submission.
   *
   * @param {Object} data - The server response data.
   */
  handleResponse(data) {
    const response = data.data;

    if (data.success) {
      this.handleSuccess(response);
    } else {
      this.handleErrorResponse(response);
    }
  }

  /**
   * Handles successful form submission.
   *
   * @param {Object} response - The success response data.
   */
  handleSuccess(response) {
    if (response.redirect) {
      window.location.href = response.redirect;
    } else {
      this.displayAlert('success', response.message || 'Form submitted successfully!');

      if (response.hideAfterSubmit) {
        this.hideForm();
      } else {
        this.resetForm();
      }
    }
  }

  /**
   * Handles an error response from the server.
   *
   * @param {Object} response - The error response data.
   */
  handleErrorResponse(response) {
    let errorMessage = '<h4 class="alert-heading">Errors detected while submitting the form</h4>';
    if (response.errors && response.errors.length > 0) {
      errorMessage += '<ul>';
      response.errors.forEach((error) => {
        errorMessage += `<li>${error}</li>`;
      });
      errorMessage += '</ul>';
    }
    this.displayAlert('danger', errorMessage);
  }

  /**
   * Handles an error during the AJAX request.
   *
   * @param {Error} error - The error object.
   */
  handleError(error) {
    console.error('Error submitting form:', error);
    this.displayAlert('danger', 'An error occurred while submitting the form.');
  }

  /**
   * Displays an alert message.
   *
   * @param {string} type - The alert type ('success' or 'danger').
   * @param {string} message - The alert message.
   */
  displayAlert(type, message) {
    this.alertPlaceholder.innerHTML = `<div class="alert alert-soft-${type}" role="alert">${message}</div>`;
  }

  /**
   * Retrieves the alert placeholder element.
   *
   * @returns {HTMLElement} - The alert placeholder element.
   */
  getAlertPlaceholder() {
    const formUniqueID = this.form.querySelector('[name="_uacaps_form_id"]').value;
    return document.getElementById(`form-alert-placeholder-${formUniqueID}`);
  }

  /**
   * Hides the form after successful submission.
   */
  hideForm() {
    this.form.remove();
  }

  /**
   * Resets the form fields.
   */
  resetForm() {
    this.form.reset();
    this.form.classList.remove('was-validated');
  }
}

export default FormHandler;
